/**
 * AgentForge agent loop (web shell).
 *
 * Plans steps via a connected catalog / Forge-endpoint brain.
 * Real OS mouse/keyboard requires AgentForge Desktop companion.
 */

import type { AgentAction } from "./types";
import { SUITE_STEP_COST_HINT_USD } from "./types";
import { chatCompletion, type InferenceTarget } from "../suite/inference";

export type LoopCallbacks = {
  onStep: (step: {
    thought?: string;
    action: AgentAction;
    status: "running" | "ok" | "error";
    error?: string;
  }) => void;
  onStatus: (msg: string) => void;
  shouldStop: () => boolean;
};

const DEMO_PLAN: { thought: string; action: AgentAction }[] = [
  {
    thought: "Review the goal and prepare operator steps.",
    action: {
      type: "note",
      summary: "Connect a catalog brain for real planning, or use desktop for OS control.",
    },
  },
  {
    thought: "Locate the primary UI target for the goal.",
    action: { type: "wait", summary: "Wait for screen settle", ms: 400 },
  },
  {
    thought: "Primary interaction toward the user goal.",
    action: { type: "click", summary: "Click primary target (desktop executes)", x: 0.5, y: 0.4 },
  },
  {
    thought: "Enter text if the goal requires typing.",
    action: { type: "type", summary: "Type goal text when required", text: "" },
  },
  {
    thought: "Finish or hand control back to the user.",
    action: { type: "done", summary: "Done" },
  },
];

function sleep(ms: number, shouldStop: () => boolean): Promise<void> {
  return new Promise((resolve) => {
    const t0 = Date.now();
    const tick = () => {
      if (shouldStop() || Date.now() - t0 >= ms) resolve();
      else requestAnimationFrame(tick);
    };
    tick();
  });
}

export function parseActionsFromText(text: string): { thought?: string; action: AgentAction }[] {
  const lines = text
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);
  const out: { thought?: string; action: AgentAction }[] = [];
  for (const line of lines.slice(0, 12)) {
    const lower = line.toLowerCase();
    if (/^done\b|finished|complete/.test(lower)) {
      out.push({ thought: line, action: { type: "done", summary: line.slice(0, 160) } });
      continue;
    }
    if (/click|tap|press/.test(lower)) {
      out.push({ thought: line, action: { type: "click", summary: line.slice(0, 160) } });
      continue;
    }
    if (/type|enter|input|write/.test(lower)) {
      out.push({ thought: line, action: { type: "type", summary: line.slice(0, 160) } });
      continue;
    }
    if (/scroll/.test(lower)) {
      out.push({ thought: line, action: { type: "scroll", summary: line.slice(0, 160) } });
      continue;
    }
    if (/hotkey|shortcut|ctrl\+|cmd\+/.test(lower)) {
      out.push({ thought: line, action: { type: "hotkey", summary: line.slice(0, 160) } });
      continue;
    }
    if (/wait|sleep|pause/.test(lower)) {
      out.push({ thought: line, action: { type: "wait", summary: line.slice(0, 160), ms: 500 } });
      continue;
    }
    out.push({ thought: line, action: { type: "note", summary: line.slice(0, 160) } });
  }
  if (!out.length) {
    out.push({
      thought: text.slice(0, 200) || "No structured steps",
      action: { type: "note", summary: "Brain returned unstructured plan" },
    });
  }
  return out;
}

async function planWithBrain(
  goal: string,
  target: InferenceTarget | null,
): Promise<{ thought?: string; action: AgentAction }[]> {
  if (!target?.baseUrl) {
    return DEMO_PLAN.map((p) => ({
      ...p,
      action: {
        ...p.action,
        text: p.action.type === "type" ? goal.slice(0, 80) : p.action.text,
      },
    }));
  }

  try {
    const content = await chatCompletion(
      target,
      [
        {
          role: "system",
          content:
            "You are AgentForge, a desktop GUI agent planner. " +
            "Given a user goal, list 3-8 short steps as plain lines. " +
            "Start lines with action verbs: click, type, scroll, hotkey, wait, done. " +
            "Plan only — do not claim you already controlled the OS.",
        },
        {
          role: "user",
          content: `Goal: ${goal}\n\nList the action plan.`,
        },
      ],
      { temperature: 0.2, maxTokens: 800 },
    );
    return parseActionsFromText(content);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return [
      {
        thought: `Brain error: ${msg}`,
        action: { type: "fail", summary: msg.slice(0, 160) },
      },
      ...DEMO_PLAN.slice(0, 2).map((p) => ({
        ...p,
        thought: `(fallback) ${p.thought}`,
        action: {
          ...p.action,
          text: p.action.type === "type" ? goal.slice(0, 80) : p.action.text,
        },
      })),
    ];
  }
}

export async function runAgentLoop(opts: {
  goal: string;
  target: InferenceTarget | null;
  billSteps: boolean;
  callbacks: LoopCallbacks;
}): Promise<{ steps: number; estimatedCostUsd: number; status: "done" | "stopped" | "failed" }> {
  const { goal, target, billSteps, callbacks } = opts;
  callbacks.onStatus(
    target
      ? `Planning with ${target.label}…`
      : "No brain connected — demo plan only. Connect HF, Cloudflare, CivitAI, or a Forge endpoint.",
  );

  let plan: { thought?: string; action: AgentAction }[];
  try {
    plan = await planWithBrain(goal, target);
  } catch (e) {
    callbacks.onStatus(e instanceof Error ? e.message : String(e));
    return { steps: 0, estimatedCostUsd: 0, status: "failed" };
  }

  let steps = 0;
  let cost = 0;
  for (const item of plan) {
    if (callbacks.shouldStop()) {
      callbacks.onStatus("Stopped by user");
      return { steps, estimatedCostUsd: cost, status: "stopped" };
    }
    callbacks.onStep({ thought: item.thought, action: item.action, status: "running" });
    if (billSteps) cost += SUITE_STEP_COST_HINT_USD;
    if (item.action.type === "wait") {
      await sleep(item.action.ms ?? 300, callbacks.shouldStop);
    } else {
      await sleep(280, callbacks.shouldStop);
    }
    if (callbacks.shouldStop()) {
      callbacks.onStep({
        thought: item.thought,
        action: item.action,
        status: "error",
        error: "stopped",
      });
      return { steps, estimatedCostUsd: cost, status: "stopped" };
    }
    callbacks.onStep({ thought: item.thought, action: item.action, status: "ok" });
    steps += 1;
    if (item.action.type === "done" || item.action.type === "fail") break;
  }
  callbacks.onStatus("Run finished");
  return { steps, estimatedCostUsd: cost, status: "done" };
}
